import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { buildNavTree, navItemsQueryOptions, type NavItem } from "@/lib/nav.queries";
import { templatesQueryOptions } from "@/lib/templates.queries";
import {
  createNavItem,
  deleteNavItem,
  reorderNavItems,
  updateNavItem,
} from "@/lib/nav.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, GripVertical, CornerDownRight, Menu } from "lucide-react";
import { FormDialog, SaveButton, useDirty } from "@/components/admin/FormDialog";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/navigation")({
  component: NavAdminPage,
});

// Radix Select rejects an empty-string item value, so "top level" uses a sentinel.
const TOP_LEVEL = "__top__";

type LinkKind = "template" | "external" | "none";

type FormState = {
  id?: string;
  label: string;
  link_kind: LinkKind;
  template_slug: string;
  external_url: string;
  parent_id: string;
  is_active: boolean;
  open_in_new_tab: boolean;
};

const empty: FormState = {
  label: "",
  link_kind: "template",
  template_slug: "",
  external_url: "",
  parent_id: TOP_LEVEL,
  is_active: true,
  open_in_new_tab: false,
};

function NavAdminPage() {
  const { data: items = [] } = useQuery(navItemsQueryOptions());
  const { data: templates = [] } = useQuery(templatesQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createNavItem);
  const update = useServerFn(updateNavItem);
  const del = useServerFn(deleteNavItem);
  const reorder = useServerFn(reorderNavItems);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const dirty = useDirty(form, open);

  const [localItems, setLocalItems] = useState<NavItem[]>(items);
  const itemsKey = useMemo(
    () =>
      [...items]
        .sort((a, b) => a.display_order - b.display_order)
        .map((n) => `${n.id}:${n.parent_id ?? ""}:${n.display_order}`)
        .join("|"),
    [items],
  );
  useEffect(() => {
    setLocalItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const { roots, children } = useMemo(() => buildNavTree(localItems), [localItems]);

  // Roots in order, each immediately followed by its children.
  const flat = useMemo(
    () =>
      roots.flatMap((r) => [
        { item: r, depth: 0 },
        ...(children.get(r.id) ?? []).map((c) => ({ item: c, depth: 1 })),
      ]),
    [roots, children],
  );

  const childCount = (id: string) => (children.get(id) ?? []).length;

  function loadEdit(n: NavItem) {
    setForm({
      id: n.id,
      label: n.label,
      link_kind: n.external_url ? "external" : n.template_slug ? "template" : "none",
      template_slug: n.template_slug ?? "",
      external_url: n.external_url ?? "",
      parent_id: n.parent_id ?? TOP_LEVEL,
      is_active: n.is_active,
      open_in_new_tab: n.open_in_new_tab,
    });
    setOpen(true);
  }

  // A parent can't be nested, so the picker is locked while the item has children.
  const editingHasChildren = !!form.id && childCount(form.id) > 0;
  const parentOptions = roots.filter((r) => r.id !== form.id);

  const save = useMutation({
    mutationFn: async () => {
      const parentId = form.parent_id === TOP_LEVEL ? null : form.parent_id;
      const siblings = localItems.filter((n) => (n.parent_id ?? null) === parentId);
      const nextOrder = siblings.length
        ? Math.max(...siblings.map((s) => s.display_order)) + 10
        : 10;
      const payload = {
        label: form.label,
        parent_id: parentId,
        template_slug: form.link_kind === "template" ? form.template_slug || null : null,
        external_url: form.link_kind === "external" ? form.external_url || null : null,
        display_order: form.id
          ? (localItems.find((x) => x.id === form.id)?.display_order ?? 0)
          : nextOrder,
        is_active: form.is_active,
        open_in_new_tab: form.open_in_new_tab,
      };
      if (form.id) await update({ data: { id: form.id, ...payload } });
      else await create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nav_items"] });
      toast.success("Saved");
      setOpen(false);
      setForm(empty);
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nav_items"] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const persistOrder = useMutation({
    mutationFn: async (changed: Array<{ id: string; display_order: number }>) => {
      if (changed.length === 0) return;
      await reorder({ data: { items: changed } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nav_items"] }),
    onError: (e: any) => {
      toast.error(e.message ?? "Reorder failed");
      setLocalItems(items);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeItem = localItems.find((x) => x.id === active.id);
    const overItem = localItems.find((x) => x.id === over.id);
    if (!activeItem || !overItem) return;

    const group = activeItem.parent_id ?? null;
    if ((overItem.parent_id ?? null) !== group) {
      toast.error("Drag within the same group — use Edit to move between groups");
      return;
    }

    const siblings = localItems
      .filter((n) => (n.parent_id ?? null) === group)
      .sort((a, b) => a.display_order - b.display_order);
    const oldIndex = siblings.findIndex((x) => x.id === activeItem.id);
    const newIndex = siblings.findIndex((x) => x.id === overItem.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(siblings, oldIndex, newIndex);
    const orders = new Map(reordered.map((n, i) => [n.id, (i + 1) * 10]));
    const changed = reordered
      .filter((n) => n.display_order !== orders.get(n.id))
      .map((n) => ({ id: n.id, display_order: orders.get(n.id)! }));
    if (changed.length === 0) return;

    setLocalItems((prev) =>
      prev.map((n) => (orders.has(n.id) ? { ...n, display_order: orders.get(n.id)! } : n)),
    );
    persistOrder.mutate(changed);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Navigation</CardTitle>
            <CardDescription>
              Drag rows to reorder within a group. Each item links to a Site Template, an
              external URL, or nothing at all (a menu group holding sub-items).
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              setForm(empty);
              setOpen(true);
            }}
          >
            <Plus className="mr-1 h-4 w-4" /> New item
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card shadow-sm">
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3 w-10"></th>
                  <th className="p-3">Label</th>
                  <th className="p-3">Link Type</th>
                  <th className="p-3">Links to</th>
                  <th className="p-3">Active</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={flat.map((f) => f.item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {flat.map(({ item, depth }) => (
                      <SortableRow
                        key={item.id}
                        item={item}
                        depth={depth}
                        childCount={childCount(item.id)}
                        onEdit={() => loadEdit(item)}
                        onDelete={() => {
                          const kids = childCount(item.id);
                          const message = kids
                            ? `Delete "${item.label}"? Its ${kids} sub-item${kids === 1 ? "" : "s"} will move to the top level.`
                            : `Delete "${item.label}"?`;
                          if (confirm(message)) remove.mutate(item.id);
                        }}
                      />
                    ))}
                    {flat.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-muted-foreground">
                          No nav items.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        </CardContent>
      </Card>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        size="md"
        icon={Menu}
        title={form.id ? "Edit nav item" : "New nav item"}
        context={form.id ? form.label : "New menu entry"}
        dirty={dirty}
        busy={save.isPending}
        asForm
        onSubmit={() => {
          if (form.label) save.mutate();
        }}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <SaveButton dirty={dirty} pending={save.isPending} disabled={!form.label} />
          </>
        }
      >
          <div className="space-y-3">
            <div>
              <Label>Label *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>

            <div>
              <Label>Parent</Label>
              <Select
                value={form.parent_id}
                onValueChange={(v) => setForm({ ...form, parent_id: v })}
                disabled={editingHasChildren}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TOP_LEVEL}>Top level</SelectItem>
                  {parentOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingHasChildren && (
                <p className="mt-1 text-xs text-muted-foreground">
                  This item has sub-items, so it has to stay at the top level. Move its
                  sub-items elsewhere first.
                </p>
              )}
            </div>

            <div>
              <Label>Link type</Label>
              <Select
                value={form.link_kind}
                onValueChange={(v) => setForm({ ...form, link_kind: v as LinkKind })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="template">Site Template</SelectItem>
                  <SelectItem value="external">External URL / route</SelectItem>
                  <SelectItem value="none">No link (menu group)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.link_kind === "template" && (
              <div>
                <Label>Site Template</Label>
                <Select
                  value={form.template_slug}
                  onValueChange={(v) => setForm({ ...form, template_slug: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select a template…" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.slug}>
                        {t.name} <span className="text-muted-foreground">({t.slug})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.link_kind === "external" && (
              <div>
                <Label>URL or app route</Label>
                <Input
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  placeholder="/news or https://…"
                />
              </div>
            )}

            {form.link_kind === "none" && (
              <p className="text-xs text-muted-foreground">
                The label opens its sub-items instead of navigating anywhere.
              </p>
            )}

            <div className="flex items-end gap-6 pt-1">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.open_in_new_tab} onCheckedChange={(v) => setForm({ ...form, open_in_new_tab: v })} />
                <Label>New tab</Label>
              </div>
            </div>
          </div>

      </FormDialog>
    </div>
  );
}

function SortableRow({
  item,
  depth,
  childCount,
  onEdit,
  onDelete,
}: {
  item: NavItem;
  depth: number;
  childCount: number;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "hsl(var(--secondary))" : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style} className="border-b hover:bg-secondary/30">
      <td className="p-3">
        <button
          type="button"
          className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-secondary active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className={cn("p-3 font-medium", depth > 0 && "pl-8 font-normal")}>
        <span className="flex items-center gap-2">
          {depth > 0 && <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          {item.label}
        </span>
      </td>
      <td className="p-3 text-muted-foreground">
        {item.template_slug ? (
          "Site Template"
        ) : item.external_url ? (
          "External URL"
        ) : childCount > 0 ? (
          <Badge variant="secondary">Menu group</Badge>
        ) : (
          "—"
        )}
      </td>
      <td className="p-3 text-muted-foreground">
        {item.template_slug ? (
          <code>/p/{item.template_slug}</code>
        ) : item.external_url ? (
          item.external_url
        ) : childCount > 0 ? (
          `${childCount} sub-item${childCount === 1 ? "" : "s"}`
        ) : (
          <span className="text-amber-600">Not linked</span>
        )}
      </td>
      <td className="p-3">{item.is_active ? "Yes" : "No"}</td>
      <td className="p-3 text-right">
        <Button variant="ghost" size="icon" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}
